import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { ArrowRight } from './Icons';
import useHaptics from '../hooks/useHaptics';
import { apiGet, apiPost } from '../lib/api';
import './Insights.css';

// ---------------------------------------------------------------------------
// Helpers — parse times from a PatternHint into the {{...}} template.
// ---------------------------------------------------------------------------
function buildTemplateFromHint(hint) {
  if (!hint) return null;
  const start = hint?.time_window?.start || hint?.start_time;
  const end = hint?.time_window?.end || hint?.end_time;
  const text =
    hint.statement ||
    hint.headline ||
    hint.summary ||
    'Your best work happened between {{HH:MM}} and {{HH:MM}} this week.';
  if (start && end) {
    let out = text;
    if (!out.includes('{{')) {
      out = out.replace(start, `{{${start}}}`).replace(end, `{{${end}}}`);
    }
    return { template: out, chartLabel: `${start} — ${end}` };
  }
  return { template: text, chartLabel: '' };
}

function renderTemplate(tpl) {
  if (!tpl) return null;
  const parts = [];
  const regex = /\{\{(.+?)\}\}/g;
  let lastIdx = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(tpl)) !== null) {
    if (match.index > lastIdx) {
      parts.push(<React.Fragment key={key++}>{tpl.slice(lastIdx, match.index)}</React.Fragment>);
    }
    parts.push(
      <span key={key++} className="gold-mark">
        {match[1]}
      </span>
    );
    lastIdx = regex.lastIndex;
  }
  if (lastIdx < tpl.length) {
    parts.push(<React.Fragment key={key++}>{tpl.slice(lastIdx)}</React.Fragment>);
  }
  return parts;
}

// ---------------------------------------------------------------------------
// Date range helpers
// ---------------------------------------------------------------------------
function buildDateRange(days = 14) {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return { rangeLabel: `${fmt(start)} — ${fmt(end)}`, start, end };
}

// Returns ISO YYYY-MM-DD for a Date object (local time).
function toISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Normalize any date string to YYYY-MM-DD (handles ISO timestamps with time).
const normalizeDateKey = (d) => (typeof d === 'string' ? d.slice(0, 10) : '');

// ---------------------------------------------------------------------------
// Build a 14-element daily-minutes series from raw logs.
// ---------------------------------------------------------------------------
function buildSeries(logs, days = 14) {
  const minutesByDate = {};
  for (const log of logs) {
    const key = normalizeDateKey(log.date);
    if (key) {
      minutesByDate[key] = (minutesByDate[key] || 0) + (log.actual_minutes || 0);
    }
  }
  const series = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    series.push(minutesByDate[toISODate(d)] || 0);
  }
  return series;
}

// Derive 3 honest evidence cells from raw logs (last 14 days).
function buildEvidence(logs) {
  const today = new Date();
  const cutoff = toISODate(new Date(today.getFullYear(), today.getMonth(), today.getDate() - 13));
  const recent = logs.filter((l) => l.date && normalizeDateKey(l.date) >= cutoff);

  const totalMinutes = Math.max(0, recent.reduce((s, l) => s + (l.actual_minutes || 0), 0));
  const daysLogged = new Set(recent.map((l) => normalizeDateKey(l.date))).size;
  const sessionCount = recent.length;

  const fmtMinutes = (m) => {
    const h = Math.floor(m / 60);
    const rem = Math.round(m % 60);
    if (h > 0) return `${h}h${rem > 0 ? ` ${rem}m` : ''}`;
    return `${Math.round(m)}m`;
  };

  return [
    { value: String(sessionCount), label: 'Sessions / 14 Days' },
    { value: String(daysLogged), label: 'Days Logged (14d)' },
    { value: fmtMinutes(totalMinutes), label: 'Total Logged (14d)' },
  ];
}

// ---------------------------------------------------------------------------
// Sparkline — Catmull-Rom-to-Bezier smooth area path, drawn in over 800ms.
// ---------------------------------------------------------------------------
function buildSmoothPath(pts) {
  if (pts.length < 2) return '';
  const f = (n) => n.toFixed(2);
  const d = [`M ${f(pts[0].x)} ${f(pts[0].y)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i], p1 = pts[i], p2 = pts[i + 1], p3 = pts[i + 2] || p2;
    const c1x = p1.x + (p2.x - p0.x) / 6, c1y = p1.y + (p2.y - p0.y) / 6;
    const c2x = p2.x - (p3.x - p1.x) / 6, c2y = p2.y - (p3.y - p1.y) / 6;
    d.push(`C ${f(c1x)} ${f(c1y)}, ${f(c2x)} ${f(c2y)}, ${f(p2.x)} ${f(p2.y)}`);
  }
  return d.join(' ');
}

function Sparkline({ values, peakIndex, width = 800, height = 280, axisLabels }) {
  // Generous padding leaves room for axis labels around the plot.
  const padLeft = 44, padRight = 14, padTop = 18, padBottom = 34;
  const plotW = width - padLeft - padRight;
  const plotH = height - padTop - padBottom;
  const min = Math.min(...values), max = Math.max(...values);
  const range = Math.max(1, max - min);
  const stepX = plotW / (values.length - 1);
  const points = values.map((v, i) => ({
    x: padLeft + i * stepX,
    y: padTop + (1 - (v - min) / range) * plotH,
  }));
  const linePath = buildSmoothPath(points);
  const baselineY = padTop + plotH;
  const last = points[points.length - 1], first = points[0];
  const areaPath = `${linePath} L ${last.x.toFixed(2)} ${baselineY} L ${first.x.toFixed(2)} ${baselineY} Z`;
  const peak = points[Math.max(0, Math.min(values.length - 1, peakIndex))];

  const pathRef = useRef(null);
  const gradientId = useMemo(() => `hx-spark-fill-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    const n = pathRef.current;
    if (!n || !n.getTotalLength) return;
    const len = n.getTotalLength();
    if (!len) return;
    n.style.transition = 'none';
    n.style.strokeDasharray = `${len}`;
    n.style.strokeDashoffset = `${len}`;
    n.getBoundingClientRect();
    n.style.transition = 'stroke-dashoffset 800ms cubic-bezier(0.16, 1, 0.3, 1)';
    n.style.strokeDashoffset = '0';
  }, [values]);

  // Y-axis tick labels: 0, mid, max (rounded minutes).
  const yMax = Math.max(1, Math.ceil(max / 30) * 30);
  const yMid = Math.round(yMax / 2);
  const yTicks = [
    { value: yMax, y: padTop },
    { value: yMid, y: padTop + plotH / 2 },
    { value: 0,    y: baselineY },
  ];

  // X-axis tick labels: first / mid / last date from axisLabels.
  const xTicks = axisLabels && axisLabels.length === 3
    ? [
        { label: axisLabels[0], x: padLeft },
        { label: axisLabels[1], x: padLeft + plotW / 2 },
        { label: axisLabels[2], x: padLeft + plotW },
      ]
    : [];

  return (
    <svg
      className="sparkline"
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--gold-400)" stopOpacity="0.34" />
          <stop offset="100%" stopColor="var(--gold-400)" stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* Subtle grid lines at each Y tick */}
      <g className="ev-grid">
        {yTicks.map((t) => (
          <line
            key={`grid-${t.value}`}
            x1={padLeft}
            x2={padLeft + plotW}
            y1={t.y}
            y2={t.y}
            stroke="var(--gold-700)"
            strokeOpacity="0.12"
            strokeWidth="1"
          />
        ))}
      </g>

      <path d={areaPath} fill={`url(#${gradientId})`} />
      <line
        x1={peak.x} x2={peak.x} y1={peak.y} y2={baselineY}
        stroke="var(--gold-500)" strokeOpacity="0.30" strokeWidth="1"
      />
      <path
        ref={pathRef} d={linePath} fill="none"
        stroke="var(--gold-300)" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
      />
      <circle
        cx={peak.x} cy={peak.y} r="6"
        fill="var(--gold-400)" stroke="var(--gold-50)" strokeWidth="1"
        className="spark-marker"
      />

      {/* Y axis labels (minutes) */}
      <g className="ev-axis ev-axis-y">
        {yTicks.map((t) => (
          <text
            key={`yl-${t.value}`}
            x={padLeft - 8}
            y={t.y}
            textAnchor="end"
            dominantBaseline="middle"
          >
            {t.value}
          </text>
        ))}
      </g>

      {/* X axis labels (dates) */}
      <g className="ev-axis ev-axis-x">
        {xTicks.map((t, i) => (
          <text
            key={`xl-${i}`}
            x={t.x}
            y={height - 10}
            textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}
          >
            {t.label}
          </text>
        ))}
      </g>
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Density chart — static visualization of daily activity (visual only).
// ---------------------------------------------------------------------------
function DateRange({ series, rangeLabel }) {
  // Use real series heights if available, else fall back to static pattern.
  const bars = useMemo(() => {
    if (series && series.length === 14) {
      const max = Math.max(...series, 1);
      return series.map((v) => Math.round((v / max) * 92) + 8);
    }
    return [40, 28, 56, 70, 64, 50, 84, 92, 76, 60, 48, 36, 30, 26];
  }, [series]);

  // Split rangeLabel at " — " for left/right ends.
  const [labelLeft, labelRight] = rangeLabel ? rangeLabel.split(' — ') : ['', ''];

  return (
    <div className="date-range">
      <div className="range-density" aria-hidden="true">
        {bars.map((h, i) => (
          <span
            key={`bar-${i}-${h}`}
            className="range-density-bar"
            style={{ height: `${Math.max(8, h)}%` }}
          />
        ))}
      </div>
      <div className="range-meta mono">
        <span>{labelLeft}</span>
        <span>{labelRight}</span>
      </div>
      <div className="range-hint eyebrow">Last 14 days</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function Insights({ userId, user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const haptics = useHaptics();

  const [hint, setHint] = useState(null);
  const [logs, setLogs] = useState(null); // null = loading, [] = empty
  const [statementVisible, setStatementVisible] = useState(true);
  const [patternsErrored, setPatternsErrored] = useState(false);

  // Stale-fetch guards — bail when a newer request has already started.
  const logsReqIdRef = useRef(0);
  const patternsReqIdRef = useRef(0);
  // Mirror of `logs` state for use inside loadPatterns without re-creating callback.
  const logsRef = useRef(null);

  // Fetch logs for the last 14 days.
  const loadLogs = useCallback(async () => {
    if (!userId) return;
    const reqId = ++logsReqIdRef.current;
    try {
      const res = await apiGet(`/api/logs/${userId}`);
      if (reqId !== logsReqIdRef.current) return;
      if (res.ok) {
        const data = await res.json().catch(() => []);
        if (reqId !== logsReqIdRef.current) return;
        setLogs(Array.isArray(data) ? data : []);
      } else {
        setLogs((prev) => (prev === null ? [] : prev));
      }
    } catch (_) {
      if (reqId !== logsReqIdRef.current) return;
      setLogs((prev) => (prev === null ? [] : prev));
    }
  }, [userId]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Keep logsRef in sync so loadPatterns can read the latest logs without a refetch.
  useEffect(() => { logsRef.current = logs; }, [logs]);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') loadLogs(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadLogs]);

  // Fetch existing patterns; if empty, trigger a fresh pass (only when logs exist).
  // Stale-fetch safe (reqId guard). On hard failure, surface a soft eyebrow;
  // an empty "no patterns yet" response is NOT an error.
  const loadPatterns = useCallback(async () => {
    if (!userId) return;
    const reqId = ++patternsReqIdRef.current;
    try {
      const res = await apiGet('/api/patterns');
      if (reqId !== patternsReqIdRef.current) return;
      if (res.ok) {
        const data = await res.json().catch(() => null);
        if (reqId !== patternsReqIdRef.current) return;
        const list = Array.isArray(data) ? data : (data?.items || data?.patterns);
        setPatternsErrored(false);
        if (Array.isArray(list) && list.length > 0) {
          setHint(list[0]);
          return;
        }
      } else if (res.status >= 500) {
        setPatternsErrored(true);
        return;
      }
      // Only call patterns/find if the user has logs to analyse.
      // Use logs already in state — single source of truth, avoids redundant fetch.
      const inState = logsRef.current;
      const haveLogs = Array.isArray(inState) && inState.length > 0;
      if (haveLogs) {
        const findRes = await apiPost('/api/patterns/find', {});
        if (reqId !== patternsReqIdRef.current) return;
        if (findRes.ok) {
          const data = await findRes.json().catch(() => null);
          if (reqId !== patternsReqIdRef.current) return;
          const next = Array.isArray(data) ? data[0] : (data?.items?.[0] || data?.patterns?.[0] || data);
          if (next && (next.statement || next.headline || next.time_window)) {
            setHint(next);
          }
          setPatternsErrored(false);
        } else if (findRes.status >= 500) {
          setPatternsErrored(true);
        }
      }
    } catch (e) {
      if (reqId !== patternsReqIdRef.current) return;
      if (process.env.NODE_ENV !== 'production') {
        console.error('Insights pattern:', e);
      }
      setPatternsErrored(true);
    }
  }, [userId]);

  useEffect(() => { loadPatterns(); }, [loadPatterns, location.key]);

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') loadPatterns(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [loadPatterns]);

  // Cross-component log-change broadcast: refetch logs when another view
  // (e.g. ActivityDetail per-log delete) signals that logs changed.
  useEffect(() => {
    const onLogsChanged = () => { loadLogs(); loadPatterns(); };
    window.addEventListener('hx:logs-changed', onLogsChanged);
    return () => window.removeEventListener('hx:logs-changed', onLogsChanged);
  }, [loadLogs, loadPatterns]);

  // Derive computed values from real logs.
  const series = useMemo(() => (logs ? buildSeries(logs) : null), [logs]);
  const hasData = series ? series.reduce((s, v) => s + v, 0) > 0 : false;
  const evidence = useMemo(() => (logs ? buildEvidence(logs) : []), [logs]);

  // Peak index = day with highest minutes in the series.
  const peakIndex = useMemo(() => {
    if (!series) return 13;
    let max = -1, idx = 13;
    series.forEach((v, i) => { if (v > max) { max = v; idx = i; } });
    return idx;
  }, [series]);

  const { rangeLabel } = useMemo(() => buildDateRange(14), []);

  // First / mid / last date labels for the chart X-axis.
  const axisDateLabels = useMemo(() => {
    const today = new Date();
    const fmt = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const first = new Date(today); first.setDate(today.getDate() - 13);
    const mid = new Date(today); mid.setDate(today.getDate() - 7);
    return [fmt(first), fmt(mid), fmt(today)];
  }, []);

  // Build the rendered hint template (only when a real hint exists).
  const tpl = useMemo(() => {
    if (!hint) return null;
    const built = buildTemplateFromHint(hint);
    return built?.template ? built : null;
  }, [hint]);

  // Cross-fade the statement if the hint changes.
  const lastHintRef = useRef(hint);
  useEffect(() => {
    if (lastHintRef.current === hint) return;
    lastHintRef.current = hint;
    setStatementVisible(false);
    const t = setTimeout(() => setStatementVisible(true), 180);
    return () => clearTimeout(t);
  }, [hint]);

  // -------------------------------------------------------------------------
  // Apply to Plans
  // -------------------------------------------------------------------------
  const [applying, setApplying] = useState(false);

  const showToast = (msg, kind = 'info') => {
    if (typeof window !== 'undefined' && typeof window.showHxToast === 'function') {
      window.showHxToast(msg, kind);
    }
  };

  const parseSuggestion = (s) => {
    if (!s) return { name: 'Deep Work — protected block', minutes: 90, time: '10:00' };
    const minMatch = String(s).match(/(\d{2,3})\s*-?\s*minute/i);
    const minutes = minMatch ? parseInt(minMatch[1], 10) : 90;
    const timeMatch = String(s).match(/(\d{1,2}):(\d{2})/);
    const time = timeMatch ? `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}` : '10:00';
    const looksLikeDeepWork = /deep work|focus block/i.test(s);
    const name = looksLikeDeepWork ? 'Deep Work — protected block' : 'Suggested block';
    return { name, minutes, time };
  };

  const handleApply = async () => {
    if (applying) return;
    haptics.trigger('medium');
    setApplying(true);
    const suggestion = hint?.suggested_change;
    const { name, minutes, time } = parseSuggestion(suggestion);

    // Guard: if parseSuggestion fell back to the default "Deep Work" name but
    // the suggestion text doesn't actually mention deep work / focus block,
    // we'd be fabricating an activity. Hand off to Plans instead.
    const isDefaultedName = name === 'Deep Work — protected block';
    const textMentionsDeepWork = /deep work|focus block/i.test(String(suggestion || ''));
    if (isDefaultedName && !textMentionsDeepWork) {
      showToast('Open Plans to add this', 'info');
      await new Promise((r) => setTimeout(r, 400));
      navigate('/app/plans', { state: { presetSuggestion: suggestion || '' } });
      setApplying(false);
      return;
    }

    try {
      await apiPost(`/api/activities/${userId}`, {
        name, target_minutes: minutes, duration_minutes: minutes,
        preferred_time: time, category: 'Focus', priority: 9,
        short_description: 'Created from an Insights pattern.',
      });
    } catch (e) {
      showToast('Could not add — please try again', 'error');
      setApplying(false);
      return;
    }

    showToast(`Added · ${name} at ${time}`, 'info');
    await new Promise((r) => setTimeout(r, 600));
    navigate('/app/plans');
  };

  // Loading state — avoid flash of empty state while fetch is in-flight.
  if (logs === null) {
    return (
      <div className="page hx-insights">
        <PageHeader user={user} />
        <div className="stagger">
          <header className="insights-title-block">
            <h1 className="h-display">Insights</h1>
            <p className="subtitle">Understand your patterns. Design your best days.</p>
          </header>
          <div className="insights-loading">
            <div className="skeleton-block" style={{ height: 160, borderRadius: 22 }} />
            <div className="skeleton-row-grid">
              <div className="skeleton-cell" /><div className="skeleton-cell" /><div className="skeleton-cell" />
            </div>
            <div className="skeleton-block" style={{ height: 120, borderRadius: 22 }} />
          </div>
        </div>
      </div>
    );
  }

  // Empty state — no activity in the last 14 days.
  if (!hasData) {
    return (
      <div className="page hx-insights">
        <PageHeader user={user} />
        <div className="stagger">
          <header className="insights-title-block">
            <h1 className="h-display">Insights</h1>
            <p className="subtitle">Understand your patterns. Design your best days.</p>
          </header>
          <div className="insights-empty">
            <div className="eyebrow-gold">NOT ENOUGH DATA YET</div>
            <h2 className="h-2">Reflect for a few days to see patterns.</h2>
            <p className="subtitle">Your insights will sharpen as you log more time.</p>
            <button className="btn-primary" onClick={() => navigate('/app/reflection')}>
              Open Reflection
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Full Insights view — real data available.
  const suggestionText = hint?.suggested_change;

  return (
    <div className="page hx-insights">
      <PageHeader user={user} />

      <div className="stagger">
        <header className="insights-title-block">
          <h1 className="h-display">Insights</h1>
          <p className="subtitle">Understand your patterns. Design your best days.</p>
        </header>

        {tpl && (
          <section className="pattern-statement">
            <div className="eyebrow-gold">
              <span className="dot" /> KEY PATTERN
            </div>
            <p className={`statement-text${statementVisible ? '' : ' is-fading'}`}>
              {renderTemplate(tpl.template)}
            </p>
          </section>
        )}

        {!tpl && patternsErrored && (
          <div className="eyebrow" role="status" style={{ opacity: 0.7 }}>
            Couldn't load patterns
          </div>
        )}

        <section className="chart-card black-card" aria-label="Activity over the last 14 days">
          <div className="chart-head eyebrow-gold">LOGGED MINUTES — LAST 14 DAYS</div>
          <Sparkline values={series} peakIndex={peakIndex} axisLabels={axisDateLabels} />
          <div className="chart-foot mono">{tpl?.chartLabel || rangeLabel}</div>
        </section>

        <div className="evidence-eyebrow eyebrow-gold">
          <span className="dot" /> EVIDENCE
        </div>

        <section
          className={`evidence-row evidence-row--3${statementVisible ? '' : ' is-fading'}`}
          aria-label="Supporting evidence"
        >
          {evidence.map((ev) => (
            <div className="evidence" key={ev.label}>
              <div className="ev-value">{ev.value}</div>
              <div className="ev-label eyebrow">{ev.label}</div>
            </div>
          ))}
        </section>

        {suggestionText && (
          <section className="suggested-change black-card">
            <div className="eyebrow-gold">
              <span className="dot" /> SUGGESTED CHANGE
            </div>
            <p className="suggestion-text">{suggestionText}</p>
            <button type="button" className="apply-link" onClick={handleApply} disabled={applying}>
              {applying ? 'Applying…' : 'Apply to Plans'} <ArrowRight size={14} />
            </button>
          </section>
        )}

        <DateRange series={series} rangeLabel={rangeLabel} />
      </div>
    </div>
  );
}
